import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";

const Seo = ({ title, description, pathname, image, article, jsonLd }) => {
  const { site } = useStaticQuery(graphql`
    query SeoQuery {
      site {
        siteMetadata {
          title
          titleTemplate
          description
          siteUrl
          image
          twitterUsername
          locale
        }
      }
    }
  `);

  const meta = site.siteMetadata;
  const buildOgUrl = (t, d) => {
    const params = new URLSearchParams();
    if (t) params.set("title", t);
    if (d) params.set("description", d);
    const qs = params.toString();
    return `${meta.siteUrl}/api/og${qs ? `?${qs}` : ""}`;
  };

  const resolvedTitle = title || meta.title;
  const resolvedDescription = description || meta.description;

  const seo = {
    title: resolvedTitle,
    description: resolvedDescription,
    image: image
      ? `${meta.siteUrl}${image}`
      : buildOgUrl(resolvedTitle, resolvedDescription),
    url: `${meta.siteUrl}${pathname || ""}`,
  };

  return (
    <Helmet
      title={seo.title}
      titleTemplate={title ? meta.titleTemplate : undefined}
    >
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <link rel="canonical" href={seo.url} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={meta.locale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      {meta.twitterUsername && <meta name="twitter:creator" content={meta.twitterUsername} />}
      <link rel="preconnect" href="https://cal.com" />
      <link rel="dns-prefetch" href="https://cal.com" />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  pathname: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default Seo;
